import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { API_URL, UPLOADS_API_URL } from './config';

const DailySalesReport = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState(7); // Default shift ID
  const navigate = useNavigate();

  const companyId = 30; // Hardcoded company ID
  const branchId = 19;  // Hardcoded branch ID
  const operatorId = 1; // Example operator ID, replace with actual ID as necessary
  const shiftStatus = 'open'; // Example shift status, adjust as needed

  useEffect(() => {
    const fetchShifts = async () => {
      const url = `${API_URL}/shift/getShiftByCompanyBranchOperatorStatus/${companyId}/${branchId}/${operatorId}/${shiftStatus}`;
      
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch shifts');
        }
        const data = await response.json();
        setShifts(data);
      } catch (error) {
        console.error('Error fetching shifts:', error);
        setError(error.message);
      }
    };

    fetchShifts();
  }, [companyId, branchId, operatorId, shiftStatus]);

  useEffect(() => {
    const fetchSalesData = async () => {
      const url = `${API_URL}/salepostrans/get_sale_pos_trans_sold_by_shift/${branchId}/${companyId}/${selectedShiftId}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setSalesData(data);
      } catch (error) {
        console.error('Error fetching sales data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, [branchId, companyId, selectedShiftId]);

  const handleShiftChange = (event) => {
    setSelectedShiftId(event.target.value);
  };

  const handleBack = () => {
    navigate('/pos'); // Navigate back to POS
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (



    <div className="min-h-screen bg-white p-4">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
        <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBack}
            className="px-6 py-2 bg-purple-600 text-black rounded hover:bg-purple-700 transition-colors"
          >
            Back to POS
          </motion.button>
          <h1 className="text-2xl font-semibold text-black">Daily Sales Report</h1>
        </div>

        <div className="mb-4">
          <label htmlFor="shiftSelect" className="block mb-2 text-sm font-medium text-gray-700">Select Shift:</label>
          <select
            id="shiftSelect"
            value={selectedShiftId}
            onChange={handleShiftChange}
            className="block w-full border border-gray-300 rounded p-2"
          >
            {shifts.map((shift) => (
              <option key={shift.shift_id} value={shift.shift_id}>
                
                {'Shift ID : '}{shift.shift_id} {'Opened : '} {shift.date_time_open}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-black font-semibold">ID</th>
                <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-black font-semibold">Date & Time</th>
                <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-black font-semibold">Cashier</th>
                <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-black font-semibold">Product</th>
                <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-black font-semibold">Quantity</th>
                <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-black font-semibold">Amount</th>
                <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-black font-semibold">Payment</th>
              </tr>
            </thead>
            <tbody>
              {salesData.length > 0 ? (
                salesData.map((sale) => (
                  <tr key={sale.payid} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 text-black">#{sale.payid}</td>
                    <td className="border border-gray-300 px-4 py-2 text-black">
                      {new Date(sale.daterec).toLocaleDateString()} {sale.timerec}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-black">{sale.username}</td>
                    <td className="border border-gray-300 px-4 py-2 text-black">{sale.product_name}</td>
                    <td className="border border-gray-300 px-4 py-2 text-black">{sale.quantity}</td>
                    <td className="border border-gray-300 px-4 py-2 text-black">${sale.amntrec.toFixed(2)}</td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className={`px-2 py-1 text-sm rounded ${
                        sale.pmode === 'CASH' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {sale.pmode}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="border border-gray-300 px-4 py-2 text-center text-black">
                    No sales data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailySalesReport;